import {
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type RedirectRequest,
} from '@azure/msal-browser'

import { env, isAuthConfigured } from '@/lib/env'
import { logger } from '@/lib/logger'

/**
 * MSAL throws on construction if clientId/tenantId are empty, so an
 * unconfigured environment gets a placeholder. `isAuthConfigured` is false in
 * that case and every entry point below turns into a no-op — the app still
 * boots so UI work is possible without a tenant.
 */
const PLACEHOLDER_GUID = '00000000-0000-0000-0000-000000000000'

export const msalConfig: Configuration = {
  auth: {
    clientId: env.entra.clientId || PLACEHOLDER_GUID,
    authority: `https://login.microsoftonline.com/${env.entra.tenantId || 'common'}`,
    redirectUri: env.entra.redirectUri,
    postLogoutRedirectUri: env.entra.postLogoutRedirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage keeps the token out of other tabs; switch to
    // 'localStorage' only if SSO across tabs is an explicit requirement.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: env.isProduction ? LogLevel.Error : LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (level === LogLevel.Error) logger.error(`[msal] ${message}`)
        else logger.debug(`[msal] ${message}`)
      },
    },
  },
}

/** Scopes requested at sign-in (identity only). */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
}

/** Scopes for calling our own backend API. */
export const apiTokenRequest = {
  scopes: env.entra.apiScopes,
}

export const msalInstance = new PublicClientApplication(msalConfig)

/**
 * Must run once before React renders. Handles the redirect response and
 * restores the active account after a full page reload.
 */
export async function initializeMsal(): Promise<void> {
  if (!isAuthConfigured) {
    logger.warn('[auth] Entra ID is not configured — running without authentication.')
    return
  }

  await msalInstance.initialize()

  const response = await msalInstance.handleRedirectPromise()
  if (response?.account) {
    msalInstance.setActiveAccount(response.account)
  } else if (!msalInstance.getActiveAccount()) {
    const [firstAccount] = msalInstance.getAllAccounts()
    if (firstAccount) msalInstance.setActiveAccount(firstAccount)
  }
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount()
}

/**
 * Silent access token for outgoing API calls. Returns null when the user is
 * signed out or the token needs interaction — callers must treat null as
 * "send the request unauthenticated and let the 401 handler take over".
 */
export async function acquireAccessToken(): Promise<string | null> {
  if (!isAuthConfigured) return null

  const account = getActiveAccount()
  if (!account) return null
  if (apiTokenRequest.scopes.length === 0) {
    logger.warn('[auth] VITE_ENTRA_API_SCOPES is empty — no API token will be attached.')
    return null
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account })
    return result.accessToken
  } catch (error: unknown) {
    if (error instanceof InteractionRequiredAuthError) {
      logger.info('[auth] Silent token acquisition requires interaction — redirecting.')
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account })
      return null
    }
    logger.error('[auth] Failed to acquire access token', error)
    return null
  }
}

export async function signIn(): Promise<void> {
  if (!isAuthConfigured) {
    logger.warn('[auth] signIn() ignored — Entra ID is not configured.')
    return
  }
  await msalInstance.loginRedirect(loginRequest)
}

export async function signOut(): Promise<void> {
  if (!isAuthConfigured) {
    logger.warn('[auth] signOut() ignored — Entra ID is not configured.')
    return
  }

  const account = getActiveAccount()

  await msalInstance.logoutRedirect({
    ...(account ? { account } : {}),
    onRedirectNavigate: () => {
      return false
    },
  })

  window.location.href = '/'
}
